import { isToday } from 'date-fns'
import { Skiptrace as Domain } from 'features/Skiptrace/domain/Skiptrace.domain'
import { Address } from 'features/valueObjects/Address'

type RawSkiptraceResultAddress = {
  address?: string
  city?: string
  state?: string
  zip?: string
}

type RawSkiptraceResultPhone = {
  number?: string | null
  type?: string | null
}

type RawSkiptraceResultRelative = {
  name?: string
  age?: string
}

type RawSkiptraceResultItem = {
  subjectName?: string
  age?: string
  bankruptcies?: string
  judgements?: string
  liens?: string
  phones?: RawSkiptraceResultPhone[]
  emails?: string[]
  possibleRelatives?: RawSkiptraceResultRelative[]
  possibleAssociates?: RawSkiptraceResultRelative[]
  addressSearch?: RawSkiptraceResultAddress[]
}

type RawCompletedSkiptrace = {
  userId?: string
  status?: string
  updatedAt?: string
  result?: {
    response?: {
      data?: RawSkiptraceResultItem[]
    }
  }
}

type MapGetSkiptraceResultResponseArgs = {
  leadId: string
  address: Domain.SubjectInfo['address']
  skiptraces: RawCompletedSkiptrace[]
  tracers: Domain.Tracer[]
}

const parseInteger = (value?: string | null): number | null => {
  if (!value) return null

  const parsed = Number.parseInt(value.trim(), 10)
  return Number.isFinite(parsed) ? parsed : null
}

const getUpdatedAtTimestamp = (updatedAt?: string): number => {
  if (!updatedAt) return 0

  const timestamp = new Date(updatedAt).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

// skiptrace2 serializes user ids from ObjectIds while the tracers GraphQL query
// still returns the logical user id. Normalizing keeps "Skipped by ..." from
// disappearing when those formats differ only by left-padding.
const normalizeTracerId = (value?: string): string =>
  (value || '').replace(/^0+/, '')

const mapAddress = (
  rawAddress: RawSkiptraceResultAddress,
): Domain.AddressHistoryEntryInfo | null => {
  const address = Address.ifValid(Address.clean)({
    line1: rawAddress.address || '',
    city: rawAddress.city || '',
    state: rawAddress.state || '',
    postalCode: rawAddress.zip || '',
  })

  if (!address) return null

  return {
    address,
    updatedAt: new Date(0),
    isNew: false,
    isSkiptraced: true,
  }
}

const getLatestCompletedSkiptrace = (
  skiptraces: RawCompletedSkiptrace[],
): RawCompletedSkiptrace | null =>
  skiptraces.reduce<RawCompletedSkiptrace | null>((latest, skiptrace) => {
    if (skiptrace?.status !== 'completed') return latest
    if (!latest) return skiptrace

    return getUpdatedAtTimestamp(skiptrace.updatedAt) > getUpdatedAtTimestamp(latest.updatedAt)
      ? skiptrace
      : latest
  }, null)

export const mapGetSkiptraceResultResponse = ({
  leadId,
  address,
  skiptraces,
  tracers,
}: MapGetSkiptraceResultResponseArgs): Domain.GetSkiptraceResultResult => {
  const latestCompletedSkiptrace = getLatestCompletedSkiptrace(skiptraces)

  if (!latestCompletedSkiptrace)
    return { result: null }

  const skiptracedAt = latestCompletedSkiptrace.updatedAt
    ? new Date(latestCompletedSkiptrace.updatedAt)
    : new Date()
  const isNew = isToday(skiptracedAt)

  const owners = (latestCompletedSkiptrace.result?.response?.data || []).map(rawOwner => ({
    name: rawOwner.subjectName?.trim() || 'Unknown',
    age: parseInteger(rawOwner.age),
    bankruptciesCount: parseInteger(rawOwner.bankruptcies),
    judgementsCount: parseInteger(rawOwner.judgements),
    liensCount: parseInteger(rawOwner.liens),
    possiblePhoneNumbers: (rawOwner.phones || [])
      .filter(phone => !!phone?.number)
      .map(phone => ({
        phoneNumber: `${phone.number}`,
        type: phone.type || null,
        updatedAt: skiptracedAt,
        isNew,
      })),
    possibleEmails: (rawOwner.emails || [])
      .filter(email => !!email)
      .map(email => ({
        email,
        updatedAt: skiptracedAt,
        isNew,
      })),
    possibleAssociates: [
      ...rawOwner.possibleRelatives || [],
      ...rawOwner.possibleAssociates || [],
    ]
      .filter(associate => !!associate?.name)
      .map(associate => ({
        name: `${associate.name}`.trim(),
        unformattedAge: associate.age?.trim() || null,
        updatedAt: skiptracedAt,
        isNew,
      })),
    addressHistory: (rawOwner.addressSearch || [])
      .map(mapAddress)
      .filter((value): value is NonNullable<typeof value> => value !== null)
      .map(item => ({
        ...item,
        updatedAt: skiptracedAt,
        isNew,
      })),
    updatedAt: skiptracedAt,
    isNew,
  }))

  const tracer = tracers.find(existingTracer =>
    normalizeTracerId(existingTracer.id) === normalizeTracerId(latestCompletedSkiptrace.userId))

  return {
    result: {
      skiptracedAt,
      // Single v4 skiptrace should stay provider-agnostic on the client. The
      // backend can fulfill "basic" with any suitable provider without leaking
      // that implementation detail into the UI contract.
      providers: [],
      tracers: tracer ? [tracer] : [],
      subject: {
        leadId,
        address,
        owner: null,
      },
      owners,
    },
  }
}
